import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { cachedFetch, cacheKeys } from '@/lib/cache'
import { useToast } from '@/hooks/use-toast'

export interface SystemPrompt {
  name: string
  version: string
  category: string
  content: string
  description: string | null
  return_type: string
  is_active: boolean
  is_latest: boolean
  created_at: string
  updated_at: string
}

interface UseSystemPromptsReturn {
  prompts: SystemPrompt[]
  loading: boolean
  error: string | null
  getPrompt: (name: string) => string
  updatePrompt: (name: string, content: string) => Promise<void>
  refreshPrompts: () => Promise<void>
}

export function useSystemPrompts(): UseSystemPromptsReturn {
  const [prompts, setPrompts] = useState<SystemPrompt[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  // Load prompts on mount
  useEffect(() => {
    loadPrompts()
  }, [])

  const loadPrompts = async () => {
    try {
      setLoading(true)
      setError(null)

      const data = await cachedFetch(
        'system_prompts:all',
        async () => {
          // First try to use the RPC function
          const { data: rpcData, error: rpcError } = await supabase
            .rpc('get_active_prompts')

          if (!rpcError && rpcData) {
            return rpcData
          }

          // Fallback to direct query if RPC doesn't exist
          const { data, error } = await supabase
            .from('system_prompts')
            .select('name, version, category, content, description, return_type, is_active, is_latest, created_at, updated_at')
            .eq('is_active', true)
            .order('name')

          if (error) {
            console.error('Direct query error:', error)
            throw error
          }

          // Log what we found
          console.log('Found active prompts:', data?.length || 0)
          data?.forEach(prompt => {
            console.log(`- ${prompt.name} (v${prompt.version}): ${prompt.content.length} chars`)
          })

          // If no active prompts, try to get latest prompts as fallback
          if (!data || data.length === 0) {
            console.log('No active prompts found, trying latest prompts...')
            const { data: latestData, error: latestError } = await supabase
              .from('system_prompts')
              .select('name, version, category, content, description, return_type, is_active, is_latest, created_at, updated_at')
              .eq('is_latest', true)
              .order('name')

            if (latestError) {
              console.error('Latest prompts query error:', latestError)
              throw latestError
            }

            console.log('Found latest prompts:', latestData?.length || 0)
            latestData?.forEach(prompt => {
              console.log(`- ${prompt.name} (v${prompt.version}): ${prompt.content.length} chars`)
            })

            return latestData
          }

          return data
        },
        5 * 60 * 1000 // Cache for 5 minutes
      )

      setPrompts(data || [])
    } catch (err) {
      console.error('Error loading system prompts:', err)
      setError(err instanceof Error ? err.message : 'Failed to load prompts')

      // Load default prompts as fallback
      setPrompts(getDefaultPrompts())
    } finally {
      setLoading(false)
    }
  }

  const getPrompt = (name: string): string => {
    const prompt = prompts.find(p => p.name === name)
    if (prompt) return prompt.content

    // Map old field names to new prompt names
    const mapping: Record<string, string> = {
      'categorization': 'question_extractor',
      'emaildrafter': 'email_styler_bernhard',
      'missionsolar': 'mission_solar_refiner',
      'neoom': 'neoom_refiner',
      'selfsolve': 'self_solve_refiner'
    }

    const mappedName = mapping[name] || name
    const mappedPrompt = prompts.find(p => p.name === mappedName)

    return mappedPrompt?.content || ''
  }

  const updatePrompt = async (name: string, content: string) => {
    try {
      // First check if user is admin
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email?.includes('admin')) {
        throw new Error('Only admins can update prompts')
      }

      // Find the current active version
      const prompt = prompts.find(p => p.name === name)
      if (!prompt) throw new Error('Prompt not found')

      const { error } = await supabase
        .from('system_prompts')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('name', name)
        .eq('version', prompt.version)
        .eq('is_active', true)

      if (error) throw error

      toast({
        title: 'Prompt aktualisiert',
        description: `${name} wurde erfolgreich gespeichert`,
      })

      // Refresh prompts and clear cache
      await refreshPrompts()
    } catch (err) {
      console.error('Error updating prompt:', err)
      toast({
        title: 'Fehler beim Speichern',
        description: err instanceof Error ? err.message : 'Prompt konnte nicht gespeichert werden',
        variant: 'destructive'
      })
      throw err
    }
  }

  const refreshPrompts = async () => {
    // Clear cache to force reload
    const cache = (window as any).queryCache
    if (cache) {
      cache.invalidate('system_prompts:all')
    }
    await loadPrompts()
  }

  return {
    prompts,
    loading,
    error,
    getPrompt,
    updatePrompt,
    refreshPrompts
  }
}

// Default prompts as fallback
function getDefaultPrompts(): SystemPrompt[] {
  return [
    {
      name: 'question_extractor',
      version: '1.0.0',
      category: 'extractor',
      content: `Extrahiere die Kundenfrage und technische Details aus der Email. Gib als JSON-Output:
{
  "question": "Die eigentliche Frage des Kunden",
  "problem_type": "Kategorie des Problems (z.B. App, Hardware, Installation)",
  "urgency": "Dringlichkeit (low/medium/high)",
  "technical_details": "Technische Details aus der Email",
  "product_info": "Informationen zum Produkt"
}`,
      description: 'Extracts customer questions from emails',
      return_type: 'json',
      is_active: true,
      is_latest: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      name: 'mission_solar_refiner',
      version: '1.0.0',
      category: 'refiner',
      content: `Verfeinere die Email-Antwort für Mission Solar. Schreibe im freundlichen, professionellen Ton von Bernhard. Strukturiere als HTML:
- Beginne mit persönlicher Anrede
- Gib klare, verständliche Antworten
- Füge technische Details hinzu wenn nötig
- Beende mit freundlicher Grußformel`,
      description: 'Refines emails for Mission Solar cases',
      return_type: 'html',
      is_active: true,
      is_latest: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      name: 'neoom_refiner',
      version: '1.0.0',
      category: 'refiner',
      content: `Verfeinere technische Antworten für NEOOM-Fälle. Schreibe im professionellen Ton für technische Eskalationen. Strukturiere als HTML:
- Technische Analyse des Problems
- Spezifische NEOOM-Lösungen
- Detaillierte Schritte zur Problemlösung
- Weiterführende technische Informationen`,
      description: 'Refines emails for Neoom technical cases',
      return_type: 'html',
      is_active: true,
      is_latest: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      name: 'self_solve_refiner',
      version: '1.0.0',
      category: 'refiner',
      content: `Verfeinere Antworten mit Schritt-für-Schritt Anleitungen zur Selbsthilfe. Strukturiere als HTML:
- Beginne mit einfacher Erklärung
- Gib nummerierte Schritte für die Lösung
- Füge Tipps und Hinweise hinzu
- Erkläre, was bei Problemen zu tun ist`,
      description: 'Refines emails for self-service solutions',
      return_type: 'html',
      is_active: true,
      is_latest: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      name: 'email_styler_bernhard',
      version: '1.0.0',
      category: 'emailstyler',
      content: `Du bist Bernhard, der freundliche und kompetente First Level Support Spezialist von Mission Solar.

Dein Stil:
- Freundlich und persönlich (Du-Form)
- Klar strukturierte Antworten
- Immer hilfsbereit und lösungsorientiert
- Gib konkrete, umsetzbare Ratschläge
- Bei Unsicherheit: ehrlich sein und alternative Wege vorschlagen

Verfasse eine professionelle Email-Antwort im HTML-Format.`,
      description: 'Email styling in Bernhard\'s tone',
      return_type: 'text',
      is_active: true,
      is_latest: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      name: 'knowledge_answering',
      version: '1.0.0',
      category: 'knowledge',
      content: `Beantworte die Kundenfrage basierend auf der Knowledge Base. Sei präzise, hilfreich und freundlich. Strukturiere die Antwort klar und verständlich.`,
      description: 'Standard prompt for knowledge base Q&A',
      return_type: 'text',
      is_active: true,
      is_latest: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ]
}